import BaseServices from './BaseServices';
import ServicesConstants from './Global';
import {
  ISConfirmPassword,
  ISResetPassword,
  ISUpdateCoreDecision,
  ISUpdateCoreIssue,
  ISUpdateCoreProject,
  ISUpdateCoreProjectSection,
  ISUpdateCoreProjectUser,
  ISUpdateCoreRisk,
  ISUpdateCoreTask,
  ISUpdateCoreTaskComment,
  ISUpdateCoreWorkspace,
  ISUpdateCoreWorkspaceUser,
  ISUpdateTaskProject,
  PlayBookRequest,
  TaskResponse,
} from './Interfaces';

export class CoreServices {
  services: BaseServices;
  constructor() {
    this.services = new BaseServices();
  }

  async createTask(
    name: string,
    dealspaceId: string,
    projectId: string,
    sectionId: string,
    referenceTaskId?: string
  ) {
    let params = {
      playbookId: projectId,
      name: name,
      sectionId: sectionId,
      dealspaceId: dealspaceId,
    };
    if (referenceTaskId) {
      (params as any).referenceTaskId = referenceTaskId;
      (params as any).isBefore = false;
    }
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.mergeflo.task.create,
        params,
        config
      )
      .then(response => response);
  }

  async getTaskDetail(taskId: string) {
    let config = {};
    let params = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.task.retrieve.replace(
          ServicesConstants.idTag,
          taskId
        ),
        params,
        config
      )
      .then(response => response);
  }

  async getPromptCategoryAll() {
    let config = {};
    let params = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.aiPrompt.promptCategoryAll,
        params,
        config
      )
      .then(response => response);
  }

  async getPromptAll() {
    let config = {};
    let params = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.aiPrompt.promptAll,
        params,
        config
      )
      .then(response => response);
  }

  async getHistoryType(historyType: string) {
    let config = {};
    let params = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.aiPrompt.history.replace(
          ServicesConstants.idTag,
          historyType
        ),
        params,
        config
      )
      .then(response => response);
  }

  async getContent(id: string) {
    let config = {};
    let params = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.aiPrompt.getContent.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async reminderEmailForProjectUser(projectUserId: string) {
    let config = {};
    let params = { mailResend: true };
    return this.services
      .put(
        ServicesConstants.ServicesConstants.core.projectUser.reminder.replace(
          ServicesConstants.idTag,
          projectUserId
        ),
        params,
        config
      )
      .then(response => response);
  }

  async inviteProject(
    username: string[],
    playbookId: string,
    invitationMessage: string
  ) {
    let config = {};
    let params = {
      projectId: playbookId,
      username: username,
      inviteMessage: invitationMessage,
    };
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.core.projectUser.create,
        params,
        config
      )
      .then(response => {
        return response;
      })
      .catch(err => {
        throw err;
      });
  }

  async createSubTask(name: string, parentId: string) {
    let params = { name: name, parentId: parentId };
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.task.createSubTask,
        params,
        config
      )
      .then(response => response);
  }

  async retrieveTask(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.task.retrieve.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async updateTask(id: string, newValues: ISUpdateCoreTask) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.core.task.update.replace(
          ServicesConstants.idTag,
          id
        ),
        newValues,
        config
      )
      .then(response => response)
      .catch(err => {
        throw err;
      });
  }

  async deleteTask(id: string) {
    let config = {};
    let params = {};
    return this.services
      .delete(
        ServicesConstants.ServicesConstants.core.task.delete.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async deleteUndoTask(id: string, body: any) {
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.task.undoRemove.replace(
          ServicesConstants.idTag,
          id
        ),
        body,
        config
      )
      .then(response => response);
  }

  async deleteSection(id: string) {
    let config = {};
    let params = {};
    return this.services
      .delete(
        ServicesConstants.ServicesConstants.core.projectSection.delete.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async listTaskAttachments(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get2(
        ServicesConstants.ServicesConstants.core.task.listAttachments.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async listTaskComments(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.task.listComments.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async getNotificationSetup() {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.notification.setup,
        params,
        config
      )
      .then(response => response);
  }

  async listUserNotifications(
    workspaceId: string,
    read: boolean,
    page: number,
    perPage: number,
    startDate: Date | null,
    endDate: Date | null,
    assignedToMe: boolean,
    taskUpdate: boolean
  ) {
    let params = {
      workspaceId: workspaceId,
      read: read,
      page: page,
      perPage: perPage,
      startDate: startDate,
      endDate: endDate,
      assignedToMe: assignedToMe,
      taskUpdate: taskUpdate,
    };
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.notification.retrieve,
        params,
        config
      )
      .then(response => response);
  }

  async markAsReadNotification(id: string) {
    let params = {};
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.notification.read.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async markAsUnReadNotification(id: string) {
    let params = {};
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.notification.unread.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async markAllAsReadNotification(ids: string[]) {
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.notification.allRead,
        ids,
        config
      )
      .then(response => response);
  }

  async unReadNotificationCount(dealspaceId: string) {
    let params = {};
    let config = {};
    return this.services.get(
      ServicesConstants.ServicesConstants.core.notification.unReadCount.replace(
        ServicesConstants.idTag,
        dealspaceId
      ),
      params,
      config
    );
  }

  async markAllAsUnreadNotification(ids: string[]) {
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.notification.allUnread,
        ids,
        config
      )
      .then(response => response);
  }

  async convertToMilestone(id: string) {
    let params = {};
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.core.task.convertToMilestone.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async milestoneToTask(id: string) {
    let params = {};
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.core.task.milestoneToTask.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async changeParentTask(id: string, parentId: string) {
    let params = { parentId: parentId };
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.mergeflo.task.changeParentTask.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async listProjectUsers(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.project.listUsers.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async listTaskProjectRelations(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.task.listProjects.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async listTaskLogs(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.task.listLogs.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async changeFamilyOrder(
    id: string,
    referenceTaskId: string,
    isBefore: boolean
  ) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.core.task.changeFamilyOrder.replace(
          ServicesConstants.idTag,
          id
        ),
        { taskId: referenceTaskId, isBefore: isBefore },
        config
      )
      .then(response => response);
  }

  async changeParentTaskAndOrder(
    parentId: string | null,
    taskId: string,
    referenceTaskId: string | null,
    isBefore: boolean
  ) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.mergeflo.task.parent.replace(
          ServicesConstants.idTag,
          taskId
        ),
        {
          parentId: parentId,
          referenceTaskId: referenceTaskId,
          isBefore: isBefore,
        },
        config
      )
      .then(response => response);
  }

  async updateSectionOrder(
    sectionId: string,
    referenceSectionId: string,
    isBefore: boolean
  ) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.core.projectSection.updateSectionOrder.replace(
          ServicesConstants.idTag,
          sectionId
        ),
        { referenceSectionId: referenceSectionId, isBefore: isBefore },
        config
      )
      .then(response => response);
  }

  async changeSectionOrder(
    taskId: string,
    referenceTaskId: string,
    projectId: string,
    isBefore: boolean
  ) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.core.task.changeSectionOrder.replace(
          ServicesConstants.idTag,
          taskId
        ),
        { taskId: referenceTaskId, isBefore: isBefore, projectId: projectId },
        config
      )
      .then(response => response);
  }

  async createIssue(parentId: string, name: string) {
    let params = { parentId: parentId, name: name };
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.issue.create,
        params,
        config
      )
      .then(response => response);
  }

  async retrieveIssue(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.issue.retrieve.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async updateTaskSection(newValues: ISUpdateTaskProject) {
    let config = {};
    return this.services
      .put(
        ServicesConstants.ServicesConstants.core.taskProject.updateSection,
        newValues,
        config
      )
      .then(response => response);
  }

  async updateIssue(id: string, newValues: ISUpdateCoreIssue) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.core.issue.update.replace(
          ServicesConstants.idTag,
          id
        ),
        newValues,
        config
      )
      .then(response => response);
  }

  async createRisk(parentId: string, name: string, workspaceId: string) {
    let params = { parentId: parentId, name: name, workspaceId: workspaceId };
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.risk.create,
        params,
        config
      )
      .then(response => response);
  }

  async retrieveRisk(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.risk.retrieve.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async updateRisk(id: string, newValues: ISUpdateCoreRisk) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.core.risk.update.replace(
          ServicesConstants.idTag,
          id
        ),
        newValues,
        config
      )
      .then(response => response);
  }

  async createDecision(playbookId: string, username: string) {
    let params = { playbookId: playbookId, username: username };
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.decision.create,
        params,
        config
      )
      .then(response => response);
  }

  async retrieveDecision(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.decision.retrieve.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async updateDecision(id: string, newValues: ISUpdateCoreDecision) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.core.decision.update.replace(
          ServicesConstants.idTag,
          id
        ),
        newValues,
        config
      )
      .then(response => response);
  }

  async createWorkspace(name: string, type: string) {
    let params = { name: name, type: type };
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.workspace.create,
        params,
        config
      )
      .then(response => response);
  }

  async retrieveWorkspace(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.workspace.retrieve.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async updateWorkspace(id: string, newValues: ISUpdateCoreWorkspace) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.core.workspace.update.replace(
          ServicesConstants.idTag,
          id
        ),
        newValues,
        config
      )
      .then(response => response);
  }

  async deleteWorkspace(id: string) {
    let config = {};
    let params = {};
    return this.services
      .delete(
        ServicesConstants.ServicesConstants.core.workspace.delete.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async createWorkspaceUser(workspaceId: string, username: string) {
    let params = { workspaceId: workspaceId, username: username };
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.workspaceUser.create,
        params,
        config
      )
      .then(response => response);
  }

  async retrieveWorkspaceUser(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.workspaceUser.retrieve.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async updateWorkspaceUser(id: string, newValues: ISUpdateCoreWorkspaceUser) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.core.workspaceUser.update.replace(
          ServicesConstants.idTag,
          id
        ),
        newValues,
        config
      )
      .then(response => response);
  }

  async deleteWorkspaceUser(id: string) {
    let config = {};
    let params = {};
    return this.services
      .delete(
        ServicesConstants.ServicesConstants.core.workspaceUser.delete.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async acceptWorkspaceUser(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.workspaceUser.accept.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async rejectWorkspaceUser(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.workspaceUser.reject.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async createProject(name: string, workspaceId: string, dealspaceId: string) {
    let params = {
      name: name,
      workspaceId: workspaceId,
      dealspaceId: dealspaceId,
    };
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.project.create,
        params,
        config
      )
      .then(response => response);
  }

  async retrieveProject(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.project.retrieve.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async updateProject(id: string, newValues: ISUpdateCoreProject) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.core.project.update.replace(
          ServicesConstants.idTag,
          id
        ),
        newValues,
        config
      )
      .then(response => response);
  }

  async deleteProject(id: string) {
    let config = {};
    let params = {};
    return this.services
      .delete(
        ServicesConstants.ServicesConstants.core.project.delete.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async listProjectSections(id: string) {
    let config = {};
    let params = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.project.listSections.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async listProjectTasks(id: string) {
    let config = {};
    let params = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.mergeflo.playbook.listTasks.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async createProjectSection(projectId: string, name: string) {
    let params = { projectId: projectId, name: name };
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.projectSection.create,
        params,
        config
      )
      .then(response => response);
  }

  async retrieveProjectSection(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.projectSection.retrieve.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async updateProjectSection(
    id: string,
    newValues: ISUpdateCoreProjectSection
  ) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.core.projectSection.update.replace(
          ServicesConstants.idTag,
          id
        ),
        newValues,
        config
      )
      .then(response => response);
  }

  async deleteProjectSection(id: string, moveSection: boolean) {
    let config = {};
    let params = { moveTasks: moveSection };
    return this.services
      .delete(
        ServicesConstants.ServicesConstants.core.projectSection.delete.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async undoDeleteSection(id: string, undoParams: any) {
    let config = {};
    let params = undoParams;
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.projectSection.undoRemove.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async createProjectUser(projectId: string, username: string) {
    let params = { projectId: projectId, username: username };
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.projectUser.create,
        params,
        config
      )
      .then(response => response);
  }

  async retrieveProjectUser(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.projectUser.retrieve.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async updateProjectUser(id: string, newValues: ISUpdateCoreProjectUser) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.core.projectUser.update.replace(
          ServicesConstants.idTag,
          id
        ),
        newValues,
        config
      )
      .then(response => response);
  }

  async deleteProjectUser(id: string) {
    let config = {};
    let params = {};
    return this.services
      .delete(
        ServicesConstants.ServicesConstants.core.projectUser.delete.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async acceptProjectUser(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.projectUser.accept.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async rejectProjectUser(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.projectUser.reject.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async createAiDocuments(conversationId: string, files: any) {
    return this.services
      .postFormDataForMultipleFiles(
        ServicesConstants.ServicesConstants.core.aiConversation.uploadDocument.replace(
          ServicesConstants.idTag,
          conversationId
        ),
        files
      )
      .then(response => response[0]);
  }

  async createAiConversation(
    name: string,
    workspaceId: string,
    projectId: string,
    queryType: string
  ) {
    let params = {
      name: name,
      workspaceId: workspaceId,
      projectId: projectId,
      queryType: queryType,
    };
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.aiConversation.create,
        params,
        config
      )
      .then(response => response);
  }

  async createTaskAttachment(taskId: string, attachments: any) {
    if (attachments && attachments.length < 1) {
      return this.services
        .postFormData(
          ServicesConstants.ServicesConstants.core.taskAttachment.create.replace(
            ServicesConstants.idTag,
            taskId
          ),
          attachments[0]
        )
        .then(response => response[0]);
    } else {
      let results: any[] = await Promise.all(
        attachments.map(async (item: any): Promise<any[]> => {
          return await this.services
            .postFormData(
              ServicesConstants.ServicesConstants.core.taskAttachment.create.replace(
                ServicesConstants.idTag,
                taskId
              ),
              item
            )
            .then(response => {
              return response[0];
            });
        })
      );
      return results;
    }
  }

  async retrieveTaskAttachment(taskAttachmentId: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.taskAttachment.retrieve.replace(
          ServicesConstants.idTag,
          taskAttachmentId
        ),
        params,
        config
      )
      .then(response => response); //deleteAttachment
  }

  async deleteTaskAttachment(taskAttachmentId: string) {
    let params = {};
    let config = {};
    return this.services
      .delete(
        ServicesConstants.ServicesConstants.core.taskAttachment.deleteAttachment.replace(
          ServicesConstants.idTag,
          taskAttachmentId
        ),
        params,
        config
      )
      .then(response => response);
  }

  async createTaskComment(taskId: string, comment: string) {
    let params = { taskId: taskId, comment: comment };
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.taskComment.create,
        params,
        config
      )
      .then(response => response);
  }

  async retrieveTaskComment(id: string) {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.taskComment.retrieve.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async updateTaskComment(id: string, newValues: ISUpdateCoreTaskComment) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.core.taskComment.update.replace(
          ServicesConstants.idTag,
          id
        ),
        newValues,
        config
      )
      .then(response => response);
  }

  async deleteTaskComment(id: string) {
    let config = {};
    let params = {};
    return this.services
      .delete(
        ServicesConstants.ServicesConstants.core.taskComment.delete.replace(
          ServicesConstants.idTag,
          id
        ),
        params,
        config
      )
      .then(response => response);
  }

  async registerUser(
    userName: string,
    name: string,
    surName: string,
    password: string,
    playbookName?: string | null
  ) {
    let params = {
      username: userName,
      name: name,
      surname: surName,
      password: password,
      dealspaceName: playbookName,
    };
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.auth.register,
        params,
        config
      )
      .then(response => response);
  }
  //resetPassword

  async resetPassword(oldPassword: string, newPassword: string) {
    let params = { oldPassword: oldPassword, newPassword: newPassword };
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.core.auth.resetPassword,
        params,
        config
      )
      .then(response => response)
      .catch(error => error);
  }

  async loginUser(email: string, password: string) {
    let params = { username: email, password: password };
    let config = {};
    return this.services
      .post2(
        ServicesConstants.ServicesConstants.core.auth.login,
        params,
        config
      )
      .then(response => response)
      .catch(error => error);
  }

  async getUserAssets() {
    let params = {};
    let config = {};
    return this.services
      .get2(
        ServicesConstants.ServicesConstants.core.user.assets,
        params,
        config
      )
      .then(response => response);
  }
  async getExcel(id: string, type: string) {
    let params = {};
    let config = {};
    return this.services
      .get2(
        ServicesConstants.ServicesConstants.core.task.excel
          .replace(ServicesConstants.idTag, id)
          .replace(ServicesConstants.excelType, type),
        params,
        config
      )
      .then(response => response);
  }

  async getUserDealspaces() {
    let params = {};
    let config = {};
    return this.services
      .get2(
        ServicesConstants.ServicesConstants.core.user.dealspaces,
        params,
        config
      )
      .then(response => response);
  }

  async getUser() {
    let params = {};
    let config = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.user.retrieve,
        params,
        config
      )
      .then(response => response);
  }

  async updateUser(newValues: Object) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.core.user.update,
        newValues,
        config
      )
      .then(response => response);
  }

  async updatePlaybookSettings(newValues: PlayBookRequest) {
    let config = {};
    return this.services
      .patch(
        ServicesConstants.ServicesConstants.core.user.playbookSettings,
        newValues,
        config
      )
      .then(response => response);
  }

  async userRegisterConfirm() {
    let config = {};
    let params = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.auth.registerConfirm,
        params,
        config
      )
      .then(response => response);
  }

  async forgotPasswordEmail(username: string) {
    let config = {};
    let params = {};
    return this.services
      .get(
        ServicesConstants.ServicesConstants.core.auth.forgotPassword + username,
        params,
        config
      )
      .then(response => response);
  }

  async confirmPassword(params: ISConfirmPassword) {
    let config = {};
    return this.services
      .post(
        ServicesConstants.ServicesConstants.core.auth.forgotPassword,
        params,
        config
      )
      .then(response => response);
  }
}
